import { Component, OnChanges, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { BackupFileDownloaderService, BackupFile } from '../shared/download/';
import { FileSizeHelper } from '../shared/utils';
import { TrackingService } from './services/tracking.service';
import { TrackingDocumentStylesheetService } from './services/tracking-document-stylesheet.service';
import { WindowRef } from '../shared/utils/window-ref.service';
import { JsonHttpService } from '../shared/http/json-http.service';
import { CurrentUserService } from '../user/services';
import { DateTimeService } from '../shared/datetime/datetime.service';

import { SvgIconComponent } from '../shared/icons/svg-icon.component';
import { TrackingDocumentShowerComponent } from './tracking-document-shower.component';

@Component({
    selector: 'ep-tracking-file-info-row',
    templateUrl: './tracking-file-info-row.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, TrackingDocumentShowerComponent]
})
export class TrackingFileInfoRowComponent implements OnInit, OnChanges {
    @Input() tooltip: string;
    @Input() header: string;
    @Input() fileType: string;
    @Input() fileIdentifier: string;
    @Input() stylesheetIdentifier: string;
    @Input() documentId: number;
    @Input() interchangeInId: number;
    @Input() showAttachmentButton: boolean;

    public hasDownloadAccess = true;
    public backupFile: BackupFile = null;
    public isLoading = true;
    public showDownloadIndicator = false;

    public permissionDownloadFile = false;
    public permissionShowFile = false;
    public permissionDownloadCdmAttachment = false;
    public permissionShowStylesheet = false;

    constructor(
        private backupFileDownloader: BackupFileDownloaderService,
        private trackingService: TrackingService,
        private trackingDocumentStylesheetService: TrackingDocumentStylesheetService,
        private windowRef: WindowRef,
        private httpService: JsonHttpService,
        private currentUserService: CurrentUserService,
        public dateTimeService: DateTimeService
    ) {
    }

    ngOnInit(): void {
        this.permissionDownloadFile = this.currentUserService.hasPermission('Tracking_Action_DownloadFile');
        this.permissionShowFile = this.currentUserService.hasPermission('Tracking_Action_ShowFileContent');
        this.permissionDownloadCdmAttachment = this.currentUserService.hasPermission('Tracking_Action_DownloadCdmAttachment');
        this.permissionShowStylesheet = this.currentUserService.hasPermission('Tracking_Action_ShowStylesheet');
    }

    async ngOnChanges(changes: any): Promise<void> {
        if (changes.documentId || changes.interchangeInId || changes.fileIdentifier) {
            await this.getFileInfo();
        }
    }

    async getFileInfo(): Promise<void> {
        try {
            this.isLoading = true;
            this.hasDownloadAccess = true;
            this.backupFile = null;
            if (this.documentId || this.interchangeInId) {
                const documentIds = this.documentId ? [this.documentId] : [];
                const interchangeInIds = this.documentId ? [] : [this.interchangeInId];
                this.hasDownloadAccess = await this.trackingService.checkDocumentDownloadAccess(documentIds, interchangeInIds);
                if (this.hasDownloadAccess && this.fileIdentifier && this.fileType) {
                    this.backupFile = await this.backupFileDownloader.getFileInfo(this.fileIdentifier, this.fileType);
                }
            }
        }
        catch (err) {
            console.error('Failed to check document access or get document info', err);
        }
        finally {
            this.isLoading = false;
        }
    }

    getReadableFileSize() {
        if (this.backupFile) {
            return FileSizeHelper.humanFileSize(this.backupFile.FileSize);
        }
        return null;
    }

    downloadFile() {
        if (!this.fileIdentifier || !this.fileType) {
            throw Error('Invalid fileIdentifier or fileType provided!');
        }

        this.showDownloadIndicator = true;
        this.backupFileDownloader.downloadFile(this.fileIdentifier, this.fileType).then(() => {
            this.showDownloadIndicator = false;
        }).catch(() => { this.showDownloadIndicator = false; });
    }

    downloadCdmAttachment() {
        this.trackingService.downloadCdmAttachment(this.documentId).then(() => { });
    }

    showStylesheet() {
        var window = this.windowRef.nativeWindow.open('', '_blank');
        this.trackingDocumentStylesheetService.getStylesheetToken(this.fileIdentifier, this.stylesheetIdentifier).then(token => {
            let url = this.httpService.generateBackendUrl('/api/tracking/stylesheet/get-stylesheet-show-data/' + token);
            window.location = url;
        });
    }

    showFileInfo() {
        alert(this.getFileInfoText());
    }

    getFileInfoText() {
        if (this.backupFile) {
            let info = `${this.backupFile?.FileName}\r\n` +
                `File size: ${this.getReadableFileSize()}\r\n` +
                `Content-Type: ${this.backupFile?.ContentType ? this.backupFile.ContentType : '<unknown>'}\r\n` +
                `Encoding: ${this.backupFile?.Encoding ? this.backupFile.Encoding : '<unknown>'}\r\n` +
                `Created: ${this.dateTimeService.getDevExpressDateTimeWithMsFunction(this.backupFile?.CreatedDate)}`;

            if (this.tooltip) {
                info = `${this.tooltip}\r\n\r\n${info}`;
            }

            return info;
        }
        return 'Unknown...';
    }
}
